import React, { useState, useEffect } from "react";
import useApi from "@/hooks/useApi";
import { Contract, Amendment } from "./contractTypes";
import { renderMarkdown } from "./markdownUtils";
import AmendmentEditor from "./AmendmentEditor";
import LoadingSpinner from "@/components/LoadingSpinner";

function fmtCurrency(val: number) {
  return val.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function fmtDate(d?: string) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    draft: "bg-slate-100 text-slate-600",
    sent: "bg-blue-100 text-blue-700",
    signed: "bg-green-100 text-green-700",
    void: "bg-red-100 text-red-600",
  };
  const labels: Record<string, string> = {
    sent: "Awaiting Signature",
    signed: "Signed",
    draft: "Draft",
    void: "Void",
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status] ?? "bg-slate-100 text-slate-500"}`}>
      {labels[status] ?? status}
    </span>
  );
}

interface Props {
  contractId: number;
  onClose: () => void;
  onRefresh: () => void;
}

export default function ContractDetail({ contractId, onClose, onRefresh }: Props) {
  const api = useApi();
  const [contract, setContract] = useState<Contract | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAmendment, setShowAmendment] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const res = await api.messaging.get(`Contract/${contractId}`);
      if (res.ok) {
        setContract(await res.json());
      }
    } catch {
      setContract(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [contractId]);

  function handlePrint() {
    window.print();
  }

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-end z-50">
        <div className="w-full max-w-2xl h-full bg-white shadow-2xl flex items-center justify-center">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-end z-50">
        <div className="w-full max-w-2xl h-full bg-white shadow-2xl flex flex-col">
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
            <h2 className="text-lg font-semibold text-slate-800">Contract</h2>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl">&times;</button>
          </div>
          <div className="flex-1 flex items-center justify-center text-slate-400">
            Failed to load contract.
          </div>
        </div>
      </div>
    );
  }

  // Parse line items
  let lineItems: Array<{ description: string; qty: number; unitPrice: number }> = [];
  try {
    if (contract.lineItemsJson) {
      const parsed = JSON.parse(contract.lineItemsJson);
      lineItems = Array.isArray(parsed) ? parsed : [];
    }
  } catch {
    lineItems = [];
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/40 flex items-center justify-end z-50">
        {/* Drawer */}
        <div className="w-full max-w-2xl h-full bg-white shadow-2xl flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 flex-shrink-0">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h2 className="text-base font-semibold text-slate-800 truncate">{contract.title}</h2>
                <StatusBadge status={contract.status} />
              </div>
              <p className="text-sm text-slate-500 mt-0.5">
                {contract.customerName} &middot; {contract.customerEmail}
              </p>
            </div>
            <div className="flex items-center gap-2 ml-4 flex-shrink-0">
              <button
                onClick={handlePrint}
                className="px-3 py-1.5 text-xs font-medium text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50"
              >
                Download / Print
              </button>
              <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none">&times;</button>
            </div>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">

            {/* Signed info */}
            {contract.status === "signed" && contract.signedAt && (
              <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-sm">
                <span className="font-semibold text-green-700">Signed</span>
                {" "}by <strong>{contract.signedByEmail || contract.customerEmail}</strong>{" "}
                on {fmtDate(contract.signedAt)}
                {contract.signedByIp && <span className="text-green-600"> from {contract.signedByIp}</span>}
              </div>
            )}

            {/* Key details */}
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <div>
                <div className="text-slate-500">Monthly Value</div>
                <div className="text-xl font-bold text-cornflower-blue-600">{fmtCurrency(contract.totalMonthlyValue)}</div>
              </div>
              {(contract.setupFee ?? 0) > 0 && (
                <div>
                  <div className="text-slate-500">Setup Fee</div>
                  <div className="font-semibold text-slate-800">{fmtCurrency(contract.setupFee!)}</div>
                </div>
              )}
              <div>
                <div className="text-slate-500">Net Terms</div>
                <div className="font-medium text-slate-800">{contract.netTerms}</div>
              </div>
              <div>
                <div className="text-slate-500">Service Period</div>
                <div className="font-medium text-slate-800">
                  {fmtDate(contract.serviceStartDate)}
                  {contract.serviceEndDate && ` – ${fmtDate(contract.serviceEndDate)}`}
                </div>
              </div>
              <div>
                <div className="text-slate-500">Sent</div>
                <div className="font-medium text-slate-800">{fmtDate(contract.sentAt)}</div>
              </div>
              <div>
                <div className="text-slate-500">Signed</div>
                <div className="font-medium text-slate-800">{fmtDate(contract.signedAt)}</div>
              </div>
            </div>

            {/* Line items */}
            {lineItems.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-2">Line Items</h3>
                <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium text-slate-600">Description</th>
                      <th className="text-right px-3 py-2 font-medium text-slate-600">Qty</th>
                      <th className="text-right px-3 py-2 font-medium text-slate-600">Unit</th>
                      <th className="text-right px-3 py-2 font-medium text-slate-600">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {lineItems.map((row, i) => (
                      <tr key={i}>
                        <td className="px-3 py-2">{row.description}</td>
                        <td className="px-3 py-2 text-right">{row.qty}</td>
                        <td className="px-3 py-2 text-right">{fmtCurrency(row.unitPrice)}</td>
                        <td className="px-3 py-2 text-right font-medium">{fmtCurrency(row.qty * row.unitPrice)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Contract body */}
            <div>
              <h3 className="text-sm font-semibold text-slate-700 mb-2">Contract</h3>
              <div
                className="border border-slate-200 rounded-lg p-5 text-sm text-slate-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(contract.body ?? "") }}
              />
            </div>

            {/* Amendments */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-700">
                  Amendments ({contract.amendments?.length ?? 0})
                </h3>
                {contract.status === "signed" && (
                  <button
                    onClick={() => setShowAmendment(true)}
                    className="text-xs font-medium text-cornflower-blue-600 hover:underline"
                  >
                    + Add Amendment
                  </button>
                )}
              </div>

              {(!contract.amendments || contract.amendments.length === 0) ? (
                <p className="text-sm text-slate-400">No amendments.</p>
              ) : (
                <div className="space-y-3">
                  {contract.amendments.map((amend: Amendment) => (
                    <div key={amend.id} className="border border-slate-200 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-slate-800 text-sm">{amend.title}</span>
                        <StatusBadge status={amend.status} />
                      </div>
                      <div className="text-xs text-slate-500 mb-2">
                        Effective: {fmtDate(amend.effectiveDate)}
                        {amend.signedAt && ` · Signed: ${fmtDate(amend.signedAt)}`}
                      </div>
                      <div className="text-sm font-medium text-cornflower-blue-600">
                        New monthly: {fmtCurrency(amend.newMonthlyValue)}
                        {amend.proratedAmount && amend.proratedAmount > 0 && (
                          <span className="text-slate-500 font-normal ml-2">
                            + {fmtCurrency(amend.proratedAmount)} prorated
                          </span>
                        )}
                      </div>
                      <div
                        className="mt-2 text-xs text-slate-600 leading-relaxed border-t border-slate-100 pt-2"
                        dangerouslySetInnerHTML={{ __html: renderMarkdown(amend.body ?? "") }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Amendment editor */}
      {showAmendment && (
        <AmendmentEditor
          contract={contract}
          onClose={() => setShowAmendment(false)}
          onSaved={() => {
            load();
            onRefresh();
          }}
        />
      )}
    </>
  );
}
